/**
 * Engines-state poller — translates OPBridge's `GET /engines/{consumer}/state`
 * snapshots into LFO / sequencer / macro runtime records so the UI sees real
 * engine state transitions (armed → running → stopping → idle) without a
 * full WS refactor.
 *
 * Sprint Session 3 slice per NEXT.md item 2 option (a) — poll now,
 * defer the real WS `engine.state` subscription to the broader Session 3
 * pass covering macro / sequencer / clock/status too.
 *
 * Behaviour:
 *   - Ticks at `POLL_HZ` while at least one bridge is connected AND
 *     either (a) we saw an active LFO on the previous tick, or (b) the
 *     LFO count went 0 → any (armed / stopping / running) within the
 *     last `TAIL_TICKS` ticks (settle time for graceful stops).
 *   - Idle ticks — when no LFOs are active and none were seen recently —
 *     stretch to `IDLE_POLL_HZ` (0.2Hz) so we're not hammering OPBridge
 *     with a snapshot every second for the null case.
 *   - Fetches from the default bridge only (first connected in the list).
 *     Multi-bridge fan-out (aggregate state across every OPBridge running
 *     the consumer) lands with the WS shift, not here.
 *   - Translates each `OpbridgeLfoStateSnapshot` to a `LfoStateMessage` and
 *     calls `recordLfoState()` directly — same direct-record pattern
 *     `recordSequencerState`/`recordMacroState` already use below.
 *   - Silently no-ops on transport errors (401, 503, network). Any
 *     `port-not-configured`-shaped LFO shows up on a connection that
 *     hasn't been fully wired, so a 4xx is expected during the migration
 *     window — logging every miss would spam the console.
 */

import { bridges, getConnection } from './store.ts';
import type { BridgeConfig } from './storage.ts';
import type {
  EngineLifecycle,
  OpbridgeLfoStateSnapshot,
  OpbridgeSequencerStateSnapshot,
  OpbridgeMacroStateSnapshot,
} from './httpClient.ts';
import { OpbridgeError } from './httpClient.ts';
import { recordSequencerState } from '../sequencer/runtime.ts';
import { recordMacroState } from '../macros/runtime.ts';
import { recordLfoState } from '../lfo/runtime.ts';
import type { LfoStateMessage, SequencerStateMessage } from '@opbuttons/schema';

const CONSUMER = 'opbuttons';
const POLL_INTERVAL_MS = 1_000;      // 1 Hz active-poll
const IDLE_POLL_INTERVAL_MS = 5_000; // 0.2 Hz idle-poll (still catches state
                                     //   flips from other clients / restarts)
const TAIL_TICKS = 3;                // keep ticking at 1Hz for 3 empty snapshots
                                     //   after activity ends (settle time)

let timer: ReturnType<typeof setTimeout> | null = null;
let stopped = false;
let idleTailCounter = 0;
/** Sequencer + macro ids seen in the previous snapshot — used to synthesize
 *  an `idle` when a runtime retires (the engine drops idle runtimes from the
 *  snapshot, so without this the PWA latches on the last non-idle state). */
const seenSeqIds = new Set<string>();
const seenMacroIds = new Set<string>();

/** Start the poller. Idempotent — calling twice is a no-op. */
export function startEnginesStatePoll(): void {
  if (timer !== null) return;
  stopped = false;
  schedule(POLL_INTERVAL_MS);
}

/** Nudge the poller to tick almost immediately + stay warm at the active
 *  cadence for a few ticks. Called right after an engine arm (sequencer /
 *  macro) so the authoritative state + step pulses appear ~150ms later
 *  instead of waiting out the ≤5s idle cadence. Idempotent + safe if the
 *  poller is stopped. */
export function pokeEnginesStatePoll(): void {
  if (stopped) return;
  idleTailCounter = TAIL_TICKS;
  if (timer !== null) { clearTimeout(timer); timer = null; }
  schedule(150);
}

/** Stop the poller. Used by tests + on tear-down. */
export function stopEnginesStatePoll(): void {
  stopped = true;
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

function schedule(delayMs: number): void {
  if (stopped) return;
  timer = setTimeout(() => {
    timer = null;
    void tick();
  }, delayMs);
}

async function tick(): Promise<void> {
  if (stopped) return;
  const cfg = pickDefaultConnected();
  if (!cfg) {
    // No connected bridge — nothing to poll. Fall to the idle cadence
    // so we stay alive to pick up when a bridge does come online.
    schedule(IDLE_POLL_INTERVAL_MS);
    return;
  }
  const conn = getConnection(cfg.id);
  if (!conn) {
    schedule(IDLE_POLL_INTERVAL_MS);
    return;
  }

  let activeCount = 0;
  try {
    const snap = await conn.httpClient().enginesState(CONSUMER);
    for (const lfo of snap.lfos) {
      const msg = translateLfo(lfo);
      if (msg) recordLfoState(msg);
    }
    const seqCount = applySequencers(snap.sequencers ?? []);
    const macroCount = applyMacros(snap.macros ?? []);
    // Keep the poll warm (1Hz) while any LFO / sequencer / macro runtime is
    // live, so armed→running transitions + step pulses / lane progress stay
    // responsive.
    activeCount = snap.lfos.length + seqCount + macroCount;
  } catch (err) {
    // 401 (token scope missing the endpoint), 503 (engines service down),
    // network abort — all expected during migration. Skip silently; the
    // poller keeps trying. Only log at debug level so devs can flip on
    // if needed. Console-warning every miss would drown out real signal.
    // Leave seenSeqIds untouched on error — a transient unreachable bridge
    // isn't evidence a sequencer retired.
    if (err instanceof OpbridgeError && err.kind === 'unauthorized') {
      // Slow the cadence so we don't hammer the endpoint with 401s.
      activeCount = 0;
    }
  }

  if (activeCount > 0) {
    idleTailCounter = TAIL_TICKS;
    schedule(POLL_INTERVAL_MS);
  } else if (idleTailCounter > 0) {
    idleTailCounter -= 1;
    schedule(POLL_INTERVAL_MS);
  } else {
    schedule(IDLE_POLL_INTERVAL_MS);
  }
}

function pickDefaultConnected(): BridgeConfig | null {
  // DEC-063: engine state comes from the primary. Peer state is
  // OPBridge's business, not OPButtons's.
  const all = bridges();
  if (all.length === 0) return null;
  const primary = all.find((cfg) => cfg.is_primary) ?? all[0]!;
  const conn = getConnection(primary.id);
  if (conn && conn.status() === 'connected') return primary;
  return null;
}

/** Translate the sequencer runtimes in an engines-state snapshot to the
 *  PWA's `sequencer.state` shape (via `recordSequencerState`), and
 *  synthesize an `idle` for any sequencer present last tick but now retired
 *  — the engine drops idle runtimes from the snapshot, so without this the
 *  PWA would latch on the last non-idle state forever. Returns the count of
 *  sequencer runtimes in the snapshot (for the poll cadence). */
function applySequencers(seqs: OpbridgeSequencerStateSnapshot[]): number {
  const present = new Set<string>();
  for (const s of seqs) {
    present.add(s.id);
    recordSequencerState({
      op: 'sequencer.state',
      sequencerId: s.id,
      state: mapSeqLifecycle(s.state),
      startBeat: s.startBeat,
      lastFiredSteps: s.lastFiredSteps ?? [],
    });
  }
  for (const id of seenSeqIds) {
    if (!present.has(id)) {
      recordSequencerState({
        op: 'sequencer.state',
        sequencerId: id,
        state: 'idle',
        startBeat: 0,
        lastFiredSteps: [],
      });
    }
  }
  seenSeqIds.clear();
  for (const id of present) seenSeqIds.add(id);
  return seqs.length;
}

/** OPBridge sequencer Lifecycle → PWA `SequencerStateMessage.state`
 *  (idle|armed|running|killing). Sequencers use `Killing`, never
 *  `Stopping`, but map `stopping` defensively rather than dropping it. */
function mapSeqLifecycle(l: EngineLifecycle): SequencerStateMessage['state'] {
  switch (l) {
    case 'idle':     return 'idle';
    case 'armed':    return 'armed';
    case 'running':  return 'running';
    case 'killing':  return 'killing';
    case 'stopping': return 'killing';
  }
}

/** Translate the macro runtimes in an engines-state snapshot to the PWA's
 *  `macro.state` shape (via `recordMacroState`), synthesizing `idle` for any
 *  macro that retired since last tick (same rationale as
 *  {@link applySequencers}). Returns the runtime count for the poll cadence.
 *  `laneProgress` (0..1) + `laneValues` (0..127) map 1:1 from OPBridge. */
function applyMacros(mm: OpbridgeMacroStateSnapshot[]): number {
  const present = new Set<string>();
  for (const m of mm) {
    present.add(m.id);
    recordMacroState({
      op: 'macro.state',
      macroId: m.id,
      state: mapSeqLifecycle(m.state),
      startBeat: m.startBeat,
      laneProgress: m.laneProgress ?? [],
      laneValues: m.laneValues ?? [],
    });
  }
  for (const id of seenMacroIds) {
    if (!present.has(id)) {
      recordMacroState({
        op: 'macro.state',
        macroId: id,
        state: 'idle',
        startBeat: 0,
        laneProgress: [],
        laneValues: [],
      });
    }
  }
  seenMacroIds.clear();
  for (const id of present) seenMacroIds.add(id);
  return mm.length;
}

/** Translate one OPBridge LFO snapshot to the legacy `LfoStateMessage`
 *  shape. Returns `null` if the LFO target isn't MIDI (OSC / DMX LFOs
 *  don't map onto the legacy protocol's `ccNumber`/`channel` fields —
 *  those consumers weren't reachable on the legacy WS bus either). */
function translateLfo(lfo: OpbridgeLfoStateSnapshot): LfoStateMessage | null {
  const target = lfo.params.target;
  if (target.type !== 'midi') return null;
  const state = mapLifecycle(lfo.state);
  if (state === null) return null;
  return {
    op: 'lfo.state',
    lfoId: lfo.id,
    state,
    // Engine emits value in `[0, 1]`; PWA UI expects MIDI 0..127.
    currentValue: Math.round(clamp01(lfo.currentValue) * 127),
    currentPhase: clamp01(lfo.currentPhase),
    ccNumber: target.ccNumber,
    channel: target.channel,
    periodBeats: lfo.params.periodBeats,
    // MIDI 0..127 center + half-range amplitude (SineLfo's UI unit).
    // OPBridge stores both in `[0, 1]` engine-space; renormalise back.
    center: Math.round(clamp01(lfo.params.center) * 127),
    amplitude: Math.round(clamp01(lfo.params.amplitude) * 127),
    waveform: lfo.params.waveform,
    loop: lfo.params.loop,
  };
}

/** Legacy `LfoStateMessage.state` is `idle | armed | running | stopping`.
 *  OPBridge additionally has `killing` for sequencer/macro flows — LFOs
 *  never enter `killing`, but if we ever see one, drop it (return null)
 *  rather than force-fitting to a legacy state. */
function mapLifecycle(l: EngineLifecycle): LfoStateMessage['state'] | null {
  switch (l) {
    case 'idle':     return 'idle';
    case 'armed':    return 'armed';
    case 'running':  return 'running';
    case 'stopping': return 'stopping';
    case 'killing':  return null;
  }
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}
